import React, { useState } from "react";
import { TableRow, TableCell, Typography, Link, Tooltip } from "@mui/material";
import { UserCard } from "./UserCard";
import { UserAvailability } from "./UserAvailability";
import moment from "moment";
import CircleIcon from "@mui/icons-material/Circle";
import LaunchIcon from "@mui/icons-material/Launch";
import { useTheme } from "@mui/material/styles";
import { grey } from "@mui/material/colors";
import { USERROLE } from "../../user/UserCommon";
import { providerPermalinkURL } from "../../atoms/atoms";
import { selectedUserIdAtom, openUserDialogAtom } from "../../atoms/usersTable";
import { useAtom } from "jotai";
import { Stack } from "@mui/system";
import _ from "lodash";
const ADMIN_LABELS = {
    [USERROLE.PROVIDER]: "Provider",
    [USERROLE.COMPANY_ADMIN_PROVIDER]: "Admin",
    [USERROLE.MANAGER_PROVIDER]: "Manager"
};
export const UserRow = ({
    userData: {
        role,
        active,
        providerActive,
        phone = "",
        firstName,
        lastName,
        email,
        permalink = "",
        photoUrl,
        skills = [],
        services = [],
        createdAt,
        lastLoginDate,
        availability
    },
    id
}) => {
    const theme = useTheme();
    const [permalinkUrl] = useAtom(providerPermalinkURL);
    const [, setSelectedUserId] = useAtom(selectedUserIdAtom);
    const [, setOpenUserDialog] = useAtom(openUserDialogAtom);
    const [openSkillsTooltip, setOpenSkillsTooltip] = useState(false);
    const [openServicesTooltip, setOpenServicesTooltip] = useState(false);
    const sortedSkills = _.sortBy(
        skills,
        (item) => item.skill?.name.toLowerCase() || ""
    );
    const sortedServices = _.sortBy(
        services,
        (item) => item.servicetype?.name.toLowerCase() || ""
    );

    const handlePermalinkClick = (event) => {
        event.stopPropagation(); //Prevents the handleRowClick function from being triggered when the permalink Icon is clicked
    };

    const handleRowClick = (id) => {
        if (!id) return;
        setSelectedUserId(id);
        setOpenUserDialog(true);
    };

    const handleSkillsTooltipOpen = () => {
        setOpenSkillsTooltip(true);
    };

    const handleSkillsTooltipClose = () => {
        setOpenSkillsTooltip(false);
    };

    const handleServicesTooltipOpen = () => {
        setOpenServicesTooltip(true);
    };

    const handleServicesTooltipClose = () => {
        setOpenServicesTooltip(false);
    };

    return (
        <TableRow key={id} hover onClick={() => handleRowClick(id)}>
            <TableCell
                sx={{
                    width: "1%",
                    textAlign: "center"
                }}
            >
                <Tooltip
                    componentsProps={{
                        tooltip: {
                            sx: {
                                fontWeight: 400,
                                fontSize: "14px !important"
                            }
                        }
                    }}
                    title={active ? "Active" : "Inactive"}
                >
                    <CircleIcon
                        sx={{
                            fontSize: "1.05rem",
                            color: active ? theme.palette.verified : grey[300]
                        }}
                    />
                </Tooltip>
            </TableCell>
            <TableCell sx={{ width: "20%" }}>
                <UserCard
                    {...{
                        phone,
                        combinedName: `${firstName} ${lastName}`,
                        email,
                        photoUrl
                    }}
                />
            </TableCell>
            <TableCell sx={{ width: "4%" }}>
                {permalink && providerActive ? (
                    <Link
                        href={`${permalinkUrl}/${permalink}`}
                        target="_blank"
                        rel="noopener"
                        onClick={handlePermalinkClick}
                        sx={{
                            color: theme.palette.text.secondary,
                            "&:hover": {
                                color: theme.palette.text.secondary
                            }
                        }}
                    >
                        <LaunchIcon />
                    </Link>
                ) : null}
            </TableCell>
            <TableCell sx={{ width: "5%" }}>
                <Typography>{ADMIN_LABELS[role]}</Typography>
            </TableCell>
            <TableCell sx={{ width: "7%", textAlign: "center" }}>
                <Tooltip
                    title={
                        sortedSkills.length > 0 ? (
                            <Stack>
                                {sortedSkills.map(
                                    (
                                        { skill: { name } } // TODO: Move to data adapter in the atom
                                    ) => (
                                        <Typography key={name}>
                                            {name}
                                        </Typography>
                                    )
                                )}
                            </Stack>
                        ) : (
                            ""
                        )
                    }
                    open={openSkillsTooltip}
                    onOpen={handleSkillsTooltipOpen}
                    onClose={handleSkillsTooltipClose}
                    placement="top"
                >
                    <Typography
                        sx={{
                            position: "relative",
                            translate: {
                                sm: "-25%",
                                md: "-30%",
                                lg: "-33%"
                            }
                        }}
                        onMouseEnter={handleSkillsTooltipOpen}
                        onMouseLeave={handleSkillsTooltipClose}
                    >
                        {skills.length}
                    </Typography>
                </Tooltip>
            </TableCell>
            <TableCell sx={{ width: "7%", textAlign: "center" }}>
                <Tooltip
                    title={
                        sortedServices.length > 0 ? (
                            <Stack>
                                {sortedServices.map(
                                    (
                                        { servicetype: { name } } // TODO: Move to data adapter in the atom
                                    ) => (
                                        <Typography key={name}>
                                            {name}
                                        </Typography>
                                    )
                                )}
                            </Stack>
                        ) : (
                            ""
                        )
                    }
                    open={openServicesTooltip}
                    onOpen={handleServicesTooltipOpen}
                    onClose={handleServicesTooltipClose}
                    placement="top"
                >
                    <Typography
                        sx={{
                            position: "relative",
                            translate: "-20%"
                        }}
                        onMouseEnter={handleServicesTooltipOpen}
                        onMouseLeave={handleServicesTooltipClose}
                    >
                        {services.length}
                    </Typography>
                </Tooltip>
            </TableCell>
            <TableCell sx={{ width: "7%" }}>
                <UserAvailability {...{ availability }} />
            </TableCell>
            <TableCell sx={{ width: "10%" }}>
                <Typography>
                    {lastLoginDate ? (
                        <>
                            {moment(lastLoginDate).format("MMM DD, Y")}
                            <br />
                            <Typography textTransform={"uppercase"}>
                                {moment(lastLoginDate).format("h:mm a")}
                            </Typography>
                        </>
                    ) : (
                        ""
                    )}
                </Typography>
            </TableCell>
            <TableCell sx={{ width: "10%" }}>
                <Typography>
                    <>
                        {moment(createdAt).format("MMM DD, Y")}
                        <br />
                        <Typography textTransform={"uppercase"}>
                            {moment(createdAt).format("h:mm a")}
                        </Typography>
                    </>
                </Typography>
            </TableCell>
        </TableRow>
    );
};
